// What a Task's Done asks for, on its card in chat and on Today: the fields its Output Contract needs from the person
// (who received the goods and when, an e-way bill's number, a drawing's open questions, a decision), sent back as the
// card's Form with the Done. A field may be a line of words, a date, a number, one of a few options, or a table whose
// lines the person adds, edits and removes (#507). A field that holds something already starts with it. A choice may
// list records by name and send back their ids, and is found by typing once it has more than a few; a table may split
// an amount, and the Form is not sent until its lines add up (#607). The Application API checks both again. A field
// may also be a file the person attaches, a PDF or a photo, such as an acknowledgement (#707); one of another kind,
// empty or over 5 MB is said so next to its field and not sent (#833).
import { useId, useState } from "react";
import {
  rupees,
  shownOption,
  typedPaise,
  type CardAsk,
  type CardFile,
  type CardForm,
  type CardOption,
} from "../api.js";
import { base64Of } from "./attach.js";
import { useWords } from "../i18n/words.js";
import type { WordKey } from "../shell/nav.js";

/** Each field's label, by its name on the card; else the card's own label, else its name in words. */
const LABELS: Readonly<Record<string, WordKey>> = {
  received_by: "cardReceivedBy",
  handed_over_on: "cardHandedOverOn",
  transport_document: "cardTransportDocument",
  eway_bill_number: "cardEwayBillNumber",
  valid_until: "cardEwayBillValidUntil",
  committed_date: "cardCommittedDate",
  gst_rate: "cardGstRate",
};

type Row = Readonly<Record<string, string>>;

/** A name in words: "received_by" reads "received by". */
const optionWords = (option: string) => option.replaceAll("_", " ");
const valueOf = (option: CardOption) => (typeof option === "string" ? option : option.value);

/** The kinds of file a card takes, by file extension. */
const CARD_FILE_TYPES: Readonly<Record<string, CardFile["mediaType"]>> = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
};
const CARD_FILE_ACCEPT = [
  ...Object.keys(CARD_FILE_TYPES).map((e) => `.${e}`),
  ...new Set(Object.values(CARD_FILE_TYPES)),
].join(",");

/** The largest file a card sends: 5 MB, as the Application API keeps it. */
const CARD_FILE_MAX_BYTES = 5 * 1024 * 1024;

/** Why a file picked cannot be sent on the card, in words (#833). */
type FileProblem = "cardFileKind" | "cardFileEmpty" | "cardFileTooLarge";

/** A file the person picked, as the card sends it; or why it cannot be: not a PDF, JPEG or PNG, empty, or too large. */
async function cardFileOf(file: File): Promise<CardFile | FileProblem> {
  const byName = CARD_FILE_TYPES[file.name.slice(file.name.lastIndexOf(".") + 1).toLowerCase()];
  const mediaType = Object.values(CARD_FILE_TYPES).find((t) => t === file.type) ?? byName;
  if (!mediaType) return "cardFileKind";
  if (file.size === 0) return "cardFileEmpty";
  if (file.size > CARD_FILE_MAX_BYTES) return "cardFileTooLarge";
  return { mediaType, data: await base64Of(file) };
}

/** More options than this are found by typing. */
const SEARCHABLE_FROM = 11;

/** What a split table's lines leave to allocate, in paise: below zero when they come to more than its total. */
const leftToAllocate = ({ split }: CardAsk, rows: readonly Row[]): number =>
  split ? rows.reduce((left, row) => left - (typedPaise(row[split.column] ?? "") ?? 0), split.total) : 0;

/** The lines of a split table, counted from 1, whose amount is typed but is not one: nothing is allocated by them. */
const unreadableLines = ({ split }: CardAsk, rows: readonly Row[]): number[] =>
  split
    ? rows.flatMap((row, i) => {
        const typed = (row[split.column] ?? "").trim();
        return typed !== "" && typedPaise(typed) === undefined ? [i + 1] : [];
      })
    : [];

/** A choice: a list to pick from, with a box to type in above it when the list is long. */
function Choice({
  ask,
  id,
  label,
  value,
  onChange,
}: {
  ask: CardAsk;
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const w = useWords();
  const [typed, setTyped] = useState("");
  const options = ask.options ?? [];
  const searchable = options.length >= SEARCHABLE_FROM;
  const words = typed.trim().toLowerCase();
  // Found by what the list shows, so typing what tells two of a name apart finds the one meant. The option chosen stays
  // listed whatever is typed, so the list never shows another than the one picked.
  const listed = (o: CardOption) => shownOption(o, options).toLowerCase();
  const shown = options.filter((o) => words === "" || valueOf(o) === value || listed(o).includes(words));
  return (
    <span className={searchable ? "choice searchable" : "choice"}>
      {searchable ? (
        <input
          type="search"
          aria-label={w.cardFindOption(label)}
          placeholder={w.cardFindOption(label)}
          value={typed}
          onChange={(event) => {
            setTyped(event.target.value);
            // Typing down to one option picks it.
            const typedWords = event.target.value.trim().toLowerCase();
            const found = options.filter((o) => listed(o).includes(typedWords));
            if (typedWords !== "" && found.length === 1 && found[0] !== undefined) onChange(valueOf(found[0]));
          }}
        />
      ) : null}
      <select
        id={id}
        aria-label={label}
        required={ask.required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{w.cardChoose}</option>
        {shown.map((o) => (
          <option key={valueOf(o)} value={valueOf(o)}>
            {shownOption(o, options)}
          </option>
        ))}
      </select>
    </span>
  );
}

/** One field of the Form, or one cell of a table's line. */
function Field({
  ask,
  id,
  label,
  value,
  onChange,
}: {
  ask: CardAsk;
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  if (ask.type === "choice") return <Choice ask={ask} id={id} label={label} value={value} onChange={onChange} />;
  // Shown as on record, not edited.
  if (ask.readOnly)
    return (
      <span id={id} className="cell" aria-label={label}>
        {value}
      </span>
    );
  return (
    <input
      id={id}
      aria-label={label}
      type={ask.type === "number" ? "number" : ask.type === "date" ? "date" : "text"}
      step={ask.type === "number" ? "any" : undefined}
      required={ask.required}
      maxLength={ask.type === "text" ? 200 : undefined}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

/** A table's lines: each cell a field, a line removed with its button, another added at the end unless it is fixed. */
function Rows({
  ask,
  id,
  label,
  rows,
  onChange,
}: {
  ask: CardAsk;
  id: string;
  label: string;
  rows: readonly Row[];
  onChange: (rows: readonly Row[]) => void;
}) {
  const w = useWords();
  const columns = ask.columns ?? [];
  const cellLabel = (c: CardAsk, n: number) => `${c.label ?? optionWords(c.name)} ${String(n)}`;
  return (
    <fieldset className="field rows" id={id}>
      <legend>{label}</legend>
      {rows.map((row, i) => (
        <div key={i} className="row">
          {columns.map((c) => (
            <Field
              key={c.name}
              ask={c}
              id={`${id}-${String(i)}-${c.name}`}
              label={cellLabel(c, i + 1)}
              value={row[c.name] ?? ""}
              onChange={(value) => onChange(rows.map((r, j) => (j === i ? { ...r, [c.name]: value } : r)))}
            />
          ))}
          <button type="button" className="linkish" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
            {w.cardRemoveRow(i + 1)}
          </button>
        </div>
      ))}
      {ask.fixed ? null : (
        <button type="button" className="btn" onClick={() => onChange([...rows, {}])}>
          {w.cardAddRow}
        </button>
      )}
      {ask.split
        ? unreadableLines(ask, rows).map((n) => {
            const amount = columns.find((c) => c.name === ask.split?.column);
            return (
              <p key={n} className="allocated off" role="alert">
                {w.cardNotAnAmount(amount ? cellLabel(amount, n) : String(n))}
              </p>
            );
          })
        : null}
      {ask.split ? <Allocated left={leftToAllocate(ask, rows)} /> : null}
    </fieldset>
  );
}

/** What a split's lines leave to allocate, or how far over its total they go. */
function Allocated({ left }: { left: number }) {
  const w = useWords();
  return (
    <p className={left === 0 ? "allocated" : "allocated off"} role="status">
      {left < 0 ? w.cardOverAllocated(rupees(-left)) : w.cardLeftToAllocate(rupees(left))}
    </p>
  );
}

export function AskForm({
  asks,
  done,
  busy,
  onDone,
}: {
  asks: readonly CardAsk[];
  /** What the Done button says. */
  done: string;
  busy: boolean;
  onDone: (form: CardForm) => void;
}) {
  const w = useWords();
  const id = useId();
  const [values, setValues] = useState<Readonly<Record<string, string>>>(() =>
    Object.fromEntries(asks.flatMap((ask) => (ask.value === undefined ? [] : [[ask.name, ask.value]]))),
  );
  // A required table starts with one empty line to fill in.
  const [tables, setTables] = useState<Readonly<Record<string, readonly Row[]>>>(() =>
    Object.fromEntries(
      asks
        .filter((ask) => ask.type === "rows")
        .map((ask) => [ask.name, ask.rows && ask.rows.length > 0 ? ask.rows : ask.required ? [{}] : []]),
    ),
  );
  const fieldLabel = (ask: CardAsk) => {
    const known = LABELS[ask.name];
    return known ? w[known] : (ask.label ?? optionWords(ask.name));
  };
  const [files, setFiles] = useState<Readonly<Record<string, CardFile>>>({});
  // Why a file picked cannot be sent, by its field: said next to it, and the card waits for another (#833).
  const [fileProblems, setFileProblems] = useState<Readonly<Record<string, FileProblem>>>({});
  // A required file is sent once it is read: until then the Done waits.
  const fileMissing =
    asks.some((ask) => ask.type === "file" && ask.required && files[ask.name] === undefined) ||
    Object.keys(fileProblems).length > 0;
  // A split whose lines do not add up to its total is not sent: what is left shows under its lines.
  const unallocated = asks.some((ask) => {
    const rows = tables[ask.name] ?? [];
    return (
      ask.split !== undefined &&
      (ask.required || rows.length > 0) &&
      (leftToAllocate(ask, rows) !== 0 || unreadableLines(ask, rows).length > 0)
    );
  });
  return (
    <form
      className="send-back"
      onSubmit={(event) => {
        event.preventDefault();
        if (unallocated || fileMissing) return;
        // A field left empty is not sent: an optional one is then absent, a required one refused by the browser. A
        // line left wholly empty is dropped.
        const fields = Object.entries(values).flatMap(([k, v]) => (v.trim() === "" ? [] : [[k, v.trim()]]));
        const lines = Object.entries(tables).map(([k, rows]) => [
          k,
          rows
            .map((r) => Object.fromEntries(Object.entries(r).map(([c, v]) => [c, v.trim()])))
            .filter((r) => Object.values(r).some((v) => v !== "")),
        ]);
        onDone(Object.fromEntries([...fields, ...lines, ...Object.entries(files)]) as CardForm);
      }}
    >
      {asks.map((ask) => {
        const label = fieldLabel(ask);
        if (ask.type === "rows")
          return (
            <Rows
              key={ask.name}
              ask={ask}
              id={`${id}-${ask.name}`}
              label={label}
              rows={tables[ask.name] ?? []}
              onChange={(rows) => setTables({ ...tables, [ask.name]: rows })}
            />
          );
        const fileProblem = ask.type === "file" ? fileProblems[ask.name] : undefined;
        if (ask.type === "file")
          return (
            <label key={ask.name} className="field" htmlFor={`${id}-${ask.name}`}>
              {label}
              <input
                id={`${id}-${ask.name}`}
                aria-label={label}
                type="file"
                accept={CARD_FILE_ACCEPT}
                aria-required={ask.required}
                onChange={(event) => {
                  const picked = event.target.files?.[0];
                  const others = Object.fromEntries(Object.entries(files).filter(([name]) => name !== ask.name));
                  const otherProblems = Object.fromEntries(
                    Object.entries(fileProblems).filter(([name]) => name !== ask.name),
                  );
                  setFiles(others);
                  setFileProblems(otherProblems);
                  if (!picked) return;
                  void cardFileOf(picked).then((file) =>
                    typeof file === "string"
                      ? setFileProblems({ ...otherProblems, [ask.name]: file })
                      : setFiles({ ...others, [ask.name]: file }),
                  );
                }}
              />
              {fileProblem ? (
                <span className="allocated off" role="alert">
                  {w[fileProblem]}
                </span>
              ) : null}
            </label>
          );
        return (
          <label key={ask.name} className="field" htmlFor={`${id}-${ask.name}`}>
            {label}
            <Field
              ask={ask}
              id={`${id}-${ask.name}`}
              label={label}
              value={values[ask.name] ?? ""}
              onChange={(value) => setValues({ ...values, [ask.name]: value })}
            />
          </label>
        );
      })}
      <div className="acts">
        <button type="submit" className="btn btn-go" disabled={busy || unallocated || fileMissing}>
          {done}
        </button>
      </div>
    </form>
  );
}
